/* Next */
import { GetStaticPaths, GetStaticProps } from 'next'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { ParsedUrlQuery } from 'querystring'
import { tProduct, isProduct } from 'src/shared/frontendTypes/product'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'

/* Functions */
import { post } from 'src/functions/fetch'
import { saveUrl } from 'src/functions/regex'
import productUrl from 'src/functions/productUrl'

/* Components */
import Product from 'src/pages/Product/index'

/* Options */
import config from 'src/config'
import { fetchSiteShopSettings } from 'src/pageBuilder/sitePageBuilder'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

interface Props {
  product: tProduct
  recommendationsDeferred?: boolean
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const ProductPage: React.FC<Props> = ({ product, recommendationsDeferred, landingPageImageAiGenerated }:Props) =>
  <Product {...{ product, recommendationsDeferred, landingPageImageAiGenerated }} />

export default ProductPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                              Static Paths & Props                               */
/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

interface IParams extends ParsedUrlQuery {
  pid: string
  alias: string
}

const wait = (ms:number) => new Promise(resolve => setTimeout(resolve, ms))

const productBuildMode = () =>
  String(process.env.PRODUCT_BUILD_MODE || '').trim().toLowerCase()

const productBuildSize = () => {
  switch (productBuildMode()) {
    case 'full': return config.cache_static_products_size
    case 'quick': return process.env.BUILD ? config.cache_static_products_size : 0
    default:
      switch(process.env.CACHE) {
        case 'static': return config.cache_static_products_size
        case 'ssr'   : return config.cache_ssr_products_size
        default      : return 1
      }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {

  const size = productBuildSize()

  if (size <= 0) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const data = await post({
    url:'build/productsUrls', 
    body:{ 
      ...config.shop, 
      size,
      token: process.env.BUILD_TOKEN,
    }, 
    local:true
  })
  
  type item = { id: number; path: string }
  const isItems = (arg: unknown): arg is item[] =>
    Array.isArray(arg) &&
    arg.every(obj =>
      typeof obj === 'object' && obj !== null &&
      hasOwnProperty(obj,'id') && typeof obj.id === 'number' &&
      hasOwnProperty(obj,'path') && typeof obj.path === 'string'
    )

  if (!isItems(data)) {
    console.error('Kann Produkt-URLs nicht laden!', data)
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const paths = data.map(({ id, path }: item) => {
    const pid = id.toString()
    const alias = saveUrl(path || pid)

    return {
      params: { pid, alias },
    }
  })

  return {
    paths: paths,
    fallback: process.env.BUILD ? false : 'blocking',
  }

}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {

  const { pid, alias } = params as IParams
  const menuId = Number(process.env.NEXT_PUBLIC_MENU)

  // Settle early failures even when the product later returns a redirect or 404.
  const siteSettingsPromise = fetchSiteShopSettings({ local: true }).then(
    value => ({ ok: true as const, value }),
    error => ({ ok: false as const, error }),
  )

  let data: unknown = null

  for (let attempt = 0; attempt < 3; attempt++) {
    data = await post({
      url:`product/${pid}`,
      body:{
        ...config.shop,
        skipAlsoPurchased: true,
        skipCrossSellSections: true,
        ...(Number.isFinite(menuId) ? { menu: menuId } : {}),
      },
      local:true,
    })

    if (isProduct(data)) break
    if (attempt < 2) await wait(250)
  }

  if (!isProduct(data)) {
    console.error('Kann Produkt nicht laden!', pid, data)
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  const canonicalProductAlias = saveUrl(data.path || String(data.id))
  if (canonicalProductAlias && canonicalProductAlias !== alias) {
    return {
      revalidate: 60,
      redirect: {
        destination: productUrl(data.id, canonicalProductAlias),
        permanent: true,
      },
    }
  }

  const siteSettingsResult = await siteSettingsPromise
  if (!siteSettingsResult.ok) throw siteSettingsResult.error
  const siteSettings = siteSettingsResult.value

  return {
    props: {
      product: data,
      recommendationsDeferred: true,
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(siteSettings.landingPageImageAiGenerated ? { landingPageImageAiGenerated: siteSettings.landingPageImageAiGenerated } : {}),
    },
    revalidate: 60,
  }

}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
